import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from "react";

// NOTE: we avoid TS and keep 'date' as either null or { startDate, endDate, key } (react-date-range shape)
const initialFilters = {
  analyzed: null, // boolean | null
  cameraAngle: null, // 'DTL' | 'FON' | null
  club: [], // string[]
  date: null, // null | { startDate: Date, endDate: Date, key?: string }
  dominantHand: null, // 'Left' | 'Right' | null
  tags: [], // string[]
  users: [], // { email, userId, userName }[]
};

const initialState = {
  activeFilters: initialFilters,
  pagination: { page: 1, rowsPerPage: 20, totalPages: 1 },
  sort: { order: "desc", sort: "videoCreated" },
  rowSelection: {},
  selectedVideos: {},
  activeClub: "D",
  quickCompareCollection1: null,
  quickCompareCollection2: null,
};

const SwingLibraryContext = createContext(null);

export function SwingLibraryProvider({ children, initial }) {
  const [activeFilters, setActiveFiltersState] = useState(
    initial?.activeFilters
      ? { ...initialFilters, ...initial.activeFilters }
      : initialState.activeFilters
  );

  const [pagination, setPaginationState] = useState(
    initial?.pagination || initialState.pagination
  );
  const [sort, setSortState] = useState(initial?.sort || initialState.sort);

  const [rowSelection, setRowSelectionState] = useState(
    initial?.rowSelection ?? initialState.rowSelection
  );
  const [selectedVideos, setSelectedVideosState] = useState(
    initial?.selectedVideos ?? initialState.selectedVideos
  );

  const [activeClub, setActiveClubState] = useState(
    initial?.activeClub || initialState.activeClub
  );

  const [quickCompareCollection1, setQuickCompareCollection1State] = useState(
    initial?.quickCompareCollection1 || initialState.quickCompareCollection1
  );

  const [quickCompareCollection2, setQuickCompareCollection2State] = useState(
    initial?.quickCompareCollection2 || initialState.quickCompareCollection2
  );

  // === API (keep names 1:1 with Zustand version) ===
  const resetActiveFilters = useCallback(() => {
    try {
      setActiveFiltersState(initialFilters);
    } catch (e) {
      console.error("resetActiveFilters failed:", e);
    }
  }, []);

  const resetPagination = useCallback(() => {
    try {
      setPaginationState(initialState.pagination);
    } catch (e) {
      console.error("resetPagination failed:", e);
    }
  }, []);

  const resetSort = useCallback(() => {
    try {
      setSortState(initialState.sort);
    } catch (e) {
      console.error("resetSort failed:", e);
    }
  }, []);

  const resetRowSelection = useCallback(() => {
    try {
      setRowSelectionState(initialState.rowSelection);
    } catch (e) {
      console.error("resetRowSelection failed:", e);
    }
  }, []);

  const resetSelectedVideos = useCallback(() => {
    try {
      setSelectedVideosState(initialState.selectedVideos);
    } catch (e) {
      console.error("resetSelectedVideos failed:", e);
    }
  }, []);

  const setActiveFilters = useCallback((patch) => {
    try {
      setActiveFiltersState((prev) => {
        const next =
          typeof patch === "function" ? patch(prev) : { ...prev, ...patch };
        // ensure date stays either null or an object
        if (next && next.date && typeof next.date === "object") {
          return { ...prev, ...next };
        }
        return { ...prev, ...next };
      });
    } catch (e) {
      console.error("setActiveFilters failed:", e);
    }
  }, []);

  const setPagination = useCallback((patch) => {
    try {
      setPaginationState((prev) => ({
        ...prev,
        ...(typeof patch === "function" ? patch(prev) : patch),
      }));
    } catch (e) {
      console.error("setPagination failed:", e);
    }
  }, []);

  const setSort = useCallback(
    (nextSort) => {
      try {
        setSortState(
          typeof nextSort === "function" ? nextSort(sort) : nextSort
        );
      } catch (e) {
        console.error("setSort failed:", e);
      }
    },
    [sort]
  );

  const setRowSelection = useCallback(
    (selection) => {
      try {
        setRowSelectionState(
          typeof selection === "function" ? selection(rowSelection) : selection
        );
      } catch (e) {
        console.error("setRowSelection failed:", e);
      }
    },
    [rowSelection]
  );

  const setSelectedVideos = useCallback((updater) => {
    try {
      setSelectedVideosState((prev) =>
        typeof updater === "function" ? updater(prev) : updater
      );
    } catch (e) {
      console.error("setSelectedVideos failed:", e);
    }
  }, []);

  const setActiveClub = useCallback((club) => {
    try {
      setActiveClubState(club);
    } catch (e) {
      console.error("setActiveClub failed:", e);
    }
  }, []);

  const setQuickCompareCollection1 = useCallback((collection) => {
    try {
      setQuickCompareCollection1State(collection);
    } catch (e) {
      console.error("setQuickCompareCollection1 failed:", e);
    }
  }, []);

  const setQuickCompareCollection2 = useCallback((collection) => {
    try {
      setQuickCompareCollection2State(collection);
    } catch (e) {
      console.error("setQuickCompareCollection2 failed:", e);
    }
  }, []);

  const value = useMemo(
    () => ({
      // state
      activeClub,
      activeFilters,
      pagination,
      rowSelection,
      selectedVideos,
      sort,
      quickCompareCollection1,
      quickCompareCollection2,
      resetActiveFilters,
      resetPagination,
      resetRowSelection,
      resetSelectedVideos,
      resetSort,
      setActiveClub,
      setActiveFilters,
      setPagination,
      setRowSelection,
      setSelectedVideos,
      setSort,
      setQuickCompareCollection1,
      setQuickCompareCollection2,
    }),
    [
      activeFilters,
      pagination,
      sort,
      rowSelection,
      selectedVideos,
      activeClub,
      quickCompareCollection1,
      quickCompareCollection2,
      resetPagination,
      resetSort,
      resetRowSelection,
      resetActiveFilters,
      resetSelectedVideos,
      setActiveFilters,
      setPagination,
      setSort,
      setRowSelection,
      setSelectedVideos,
      setActiveClub,
      setQuickCompareCollection1,
      setQuickCompareCollection2,
    ]
  );

  return (
    <SwingLibraryContext.Provider value={value}>
      {children}
    </SwingLibraryContext.Provider>
  );
}

// same hook name style you already use elsewhere
export function useSwingLibraryStore() {
  const ctx = useContext(SwingLibraryContext);
  if (!ctx)
    throw new Error(
      "useSwingLibraryStore must be used within a SwingLibraryProvider"
    );
  return ctx;
}
